import { TextInput, TextInputProps, Layer } from '@carbon/react';
import classNames from 'classnames';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCaretPreservation } from 'lib/hooks/useCaretPreservation';
import style from './input-with-icon.module.scss';

export interface InputWithIconProps extends Omit<
  TextInputProps,
  'onChange' | 'light'
> {
  icon: React.ReactNode;
  onIconClick: () => void;
  onChange: (number: number | string) => void;
  inputType: 'number' | 'text';
  light?: boolean;
}

export const InputWithIcon = ({
  icon,
  onIconClick,
  value,
  onChange,
  inputType = 'text',
  light = false,
  ...props
}: InputWithIconProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [inputValue, setInputValue] = useState(value);
  // Keep the caret where the user is editing after number mode re-formats the
  // value with thousands separators, instead of letting it jump to the end.
  const { inputRef, rememberCaret } = useCaretPreservation(inputValue);

  useEffect(() => {
    const updateValue = (incomingValue: string) => {
      if (!incomingValue) return;
      if (inputType === 'text') {
        setInputValue(incomingValue);
        return;
      }

      // Don't overwrite local state if it already represents the same number.
      // This preserves intermediate typing states like "1.0" or "1.00".
      const currentStr = inputValue?.toString() ?? '';
      const currentNumeric = Number(currentStr);
      const incomingNumeric = Number(incomingValue);
      if (
        currentStr !== '' &&
        !isNaN(currentNumeric) &&
        !isNaN(incomingNumeric) &&
        currentNumeric === incomingNumeric
      ) {
        return;
      }

      // Allow only numbers, optional single leading minus, and optional single decimal point
      // Remove all non-digit, non-dot, non-minus characters
      let currentValue = incomingValue.replace(/[^0-9.-]/g, '');

      // If "-" not at start, remove all and prepend to start only if present in original input.
      if (currentValue.includes('-')) {
        currentValue = currentValue.replace(/-/g, '');
        currentValue = '-' + currentValue;
      }

      // Ensure only one '.'
      const idx = currentValue.indexOf('.');
      if (idx !== -1) {
        // Only keep first decimal, remove any others after
        currentValue =
          currentValue.substring(0, idx + 1) +
          currentValue.substring(idx + 1).replace(/\./g, '');
      }

      setInputValue(currentValue);
    };

    updateValue(value?.toString() ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputType, value]);

  useLayoutEffect(() => {
    if (containerRef.current && buttonRef.current) {
      const inputWrapper = containerRef.current.querySelector(
        '.bx--text-input__field-wrapper'
      );
      if (inputWrapper && buttonRef.current.parentElement !== inputWrapper) {
        inputWrapper.appendChild(buttonRef.current);
      }
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let currentValue = e.target.value;

    if (inputType === 'text') {
      setInputValue(currentValue);
      onChange(currentValue);
      return;
    }

    // Anchor the caret so it can be restored after the value is re-formatted
    // with commas, instead of jumping to the end (e.g. deleting a digit next to
    // a comma).
    rememberCaret();

    // Allow only numbers, optional single leading minus, and optional single decimal point
    // Remove all non-digit, non-dot, non-minus characters
    currentValue = currentValue.replace(/[^0-9.-]/g, '');

    // If "-" not at start, remove all and prepend to start only if present in original input.
    if (currentValue.includes('-')) {
      currentValue = currentValue.replace(/-/g, '');
      currentValue = '-' + currentValue;
    }

    // Ensure only one '.'
    const idx = currentValue.indexOf('.');
    if (idx !== -1) {
      // Only keep first decimal, remove any others after
      currentValue =
        currentValue.substring(0, idx + 1) +
        currentValue.substring(idx + 1).replace(/\./g, '');
    }

    setInputValue(currentValue);
    const parsedValue = Number(currentValue);

    if (
      currentValue !== '' &&
      currentValue !== '-' &&
      currentValue !== '.' &&
      !isNaN(parsedValue)
    ) {
      onChange(parsedValue);
    } else {
      onChange('');
    }
  };

  const textInput = (
    <TextInput
      ref={inputRef}
      value={formatInputValue(inputValue?.toString() ?? '')}
      onChange={handleChange}
      {...props}
    />
  );

  return (
    <>
      <div
        ref={containerRef}
        className={classNames(style.numberInputWithIcon, {
          [style.hasError]: props?.invalid,
        })}
      >
        {light ? <Layer>{textInput}</Layer> : textInput}
        <button
          ref={buttonRef}
          className={style.iconButton}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onIconClick();
          }}
        >
          {icon}
        </button>
      </div>
    </>
  );
};

export const formatInputValue = (inputValue: string) => {
  if (!inputValue) return '';
  // Strip leading zeros except when value is exactly "0" or starts with "0."
  const ip = inputValue.toString();
  // Keep the negative sign if present
  const isNegative = ip.startsWith('-');
  let [intPart, decPart] = ip.replace(/^(-?)(0+)(\d)/, '$1$3').split('.');

  // Prevent numbers like "03" or "-03"
  // allow "-0" or "0" or "-0.xxx" or "0.xxx"
  if (intPart.length > 1 && intPart.startsWith('0') && !decPart) {
    intPart = isNegative ? '-0' : '0';
  }

  // Add thousands separator to integer part if not '-0'
  // If intPart is '-0', don't format
  let formattedInt = intPart;
  if (isNegative && intPart === '-0') {
    formattedInt = '-0';
  } else if (intPart !== '0' && intPart !== '-0') {
    // Remove the negative sign for formatting, append after
    formattedInt =
      (isNegative ? '-' : '') +
      intPart.replace('-', '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  return decPart !== undefined ? formattedInt + '.' + decPart : formattedInt;
};
